/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React, { useState } from "react";
import { Box } from "@mui/material";
import EChart from "components/Shared/EChart";
import ChartCard from "components/Shared/ChartCard";
import { filterByPeriod } from "components/Shared/chartUtils";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const IntradayPatternPanel = ({ detail, summary }) => {
  const [period, setPeriod] = useState("all");
  if (!detail) return null;
  const { barsPerDay = [], dowHourHeatmap = [], anomalousDays = [] } = detail;
  const medianBarsPerDay = summary?.medianBarsPerDay ?? null;
  const shortDayCount = summary?.shortDayCount ?? null;
  const totalDayCount = summary?.totalDayCount ?? null;
  const shortDayFraction = summary?.shortDayFraction ?? null;

  const filteredBars = filterByPeriod(barsPerDay, period);

  const barsOption = {
    animation: false,
    tooltip: { trigger: "axis" },
    grid: { left: 50, right: 30, top: 20, bottom: 30 },
    xAxis: { type: "category", data: filteredBars.map((d) => d.date) },
    yAxis: { type: "value", name: "Bars / day" },
    series: [{ name: "Bars/day", type: "bar", data: filteredBars.map((d) => d.bars), barMaxWidth: 4 }],
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const maxValue = Math.max(...dowHourHeatmap.map((d) => d.value || 0), 1);
  const heatmapOption = {
    animation: false,
    tooltip: {
      formatter: (p) => `${DAYS[p.value[1]]} ${p.value[0]}:00 — ${p.value[2]?.toLocaleString?.() ?? p.value[2]}`,
    },
    grid: { left: 50, right: 30, top: 20, bottom: 50 },
    xAxis: { type: "category", data: hours, name: "Hour" },
    yAxis: { type: "category", data: DAYS },
    visualMap: {
      min: 0,
      max: maxValue,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      calculable: true,
      inRange: { color: ["#0d1117", "#2f6b33"] },
      textStyle: { color: "rgba(255, 255, 255, 0.7)" },
    },
    series: [
      {
        type: "heatmap",
        data: dowHourHeatmap.map((d) => [d.hour, d.dayOfWeek, d.value]),
        itemStyle: { borderWidth: 0 },
      },
    ],
  };

  const fractionPct = shortDayFraction != null ? (shortDayFraction * 100).toFixed(1) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {medianBarsPerDay != null && shortDayCount != null && (
        <Box sx={{ fontSize: 13, opacity: 0.85 }}>
          Median {medianBarsPerDay} bars/day · {shortDayCount}
          {totalDayCount ? ` of ${totalDayCount}` : ""} short days
          {fractionPct != null ? ` (${fractionPct}%)` : ""}
        </Box>
      )}
      <ChartCard title="Bars per day" period={period} onPeriodChange={setPeriod}>
        <EChart option={barsOption} height={220} />
      </ChartCard>
      <ChartCard title="Day-of-week × Hour activity">
        <EChart option={heatmapOption} height={280} />
      </ChartCard>
      {anomalousDays.length > 0 && (
        <Box sx={{ mt: 1, fontSize: 13 }}>
          <Box sx={{ opacity: 0.7 }}>Top anomalous days (by intraday return)</Box>
          {anomalousDays.map((d, i) => (
            <Box key={i}>{d.date}: {d.return != null ? `${(d.return * 100).toFixed(2)}%` : "—"}</Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default IntradayPatternPanel;
